import {
  PREDICTIONS_API_ENDPOINTS,
  getCompetitionDetailEndpoint,
  getGameDetailEndpoint,
} from "@/features/predictions/config/predictionsApiPath";
import { toCompetition, toMatch, toPrediction } from "@/features/predictions/services/mappers";
import type { ApiCompetition, Competition } from "@/features/predictions/types/competition.type";
import type { GetMatchesParams } from "@/features/predictions/types/filters.type";
import type { ApiMatch, Match } from "@/features/predictions/types/match.type";
import type { ApiPrediction, Prediction } from "@/features/predictions/types/prediction.type";
import { fetcher } from "@/shared/lib/fetcher.client";

type ApiMatchesListResponse = {
  matches: ApiMatch[]
  predictions: ApiPrediction[]
  metadata: {
    total: number
    to_predict_count: number
    upcoming_count: number
    live_count: number
    finished_count: number
  }
};

type ApiMatchDetailResponse = {
  match: ApiMatch
  prediction: ApiPrediction | null
};

export type MatchesListResult = {
  matches: Match[]
  predictionsByMatchId: Map<string, Prediction>
  metadata: {
    total: number
    toPredictCount: number
    upcomingCount: number
    liveCount: number
    finishedCount: number
  }
};

export type MatchDetailResult = {
  match: Match
  prediction: Prediction | null
};

const buildMatchesParams = (params: GetMatchesParams = {}): Record<string, unknown> => ({
  competition_id: params.competitionId,
  filter: params.filter,
  phase: params.phase,
  page: params.page,
  per_page: params.perPage,
});

export const matchesService = {
  async getActiveCompetition(): Promise<Competition | null> {
    const response = await fetcher.get<{ competition: ApiCompetition | null }>(
      PREDICTIONS_API_ENDPOINTS.ACTIVE_COMPETITION,
    );
    return response.data.competition ? toCompetition(response.data.competition) : null;
  },

  async getCompetition(competitionId: string): Promise<Competition> {
    const response = await fetcher.get<{ competition: ApiCompetition }>(
      getCompetitionDetailEndpoint(competitionId),
    );
    return toCompetition(response.data.competition);
  },

  async getMatches(params: GetMatchesParams = {}): Promise<MatchesListResult> {
    const response = await fetcher.get<ApiMatchesListResponse>(
      PREDICTIONS_API_ENDPOINTS.GAMES,
      { params: buildMatchesParams(params) },
    );
    const predictionsByMatchId = new Map<string, Prediction>();
    response.data.predictions.forEach((apiPrediction) => {
      const prediction = toPrediction(apiPrediction);
      predictionsByMatchId.set(prediction.matchId, prediction);
    });
    return {
      matches: response.data.matches.map(toMatch),
      predictionsByMatchId,
      metadata: {
        total: response.data.metadata.total,
        toPredictCount: response.data.metadata.to_predict_count,
        upcomingCount: response.data.metadata.upcoming_count,
        liveCount: response.data.metadata.live_count ?? 0,
        finishedCount: response.data.metadata.finished_count,
      },
    };
  },

  async getMatch(matchId: string): Promise<MatchDetailResult> {
    const response = await fetcher.get<ApiMatchDetailResponse>(
      getGameDetailEndpoint(matchId),
    );
    return {
      match: toMatch(response.data.match),
      prediction: response.data.prediction ? toPrediction(response.data.prediction) : null,
    };
  },
};
